// Page d'arrivée d'un lien d'invitation (D-075) : /i/<jeton>, sans compte.
//  - lien personnel ou de rôle : l'adresse e-mail, le code reçu, et le compte est relié ;
//  - lien d'équipe : la personne se présente, le coach valide ;
//  - lien de la présidence d'un club créé en libre-service (D-088), réservé à l'adresse de la
//    demande : juste après la création, le compte s'ouvre sans code (sinon, le code habituel).
// Tutoiement entre joueuses et bénévoles ; vouvoiement pour les parents et les rôles donnés par le bureau.
import { claimDirect, claimLink, inviteLinkInfo, requestJoin, type LinkInfo } from '../lib/api';
import { sendEmailCode, verifyEmailCode } from '../lib/auth';
import { supabase } from '../lib/supabase';
import { $, html, mount, type Html } from './dom';
import { applyBrand, clubMark } from './theme';
import { deClub } from '../domain/signup';
import { consentNote, dataHref, legalFootLink } from './legal-links';
import { freeLinkNote, type FreeLinkNote } from '../lib/gratuite-api';
import { leClub } from '../domain/gratuite';
import './gratuite.css';

const ROLE_WORD = { admin: 'membre du bureau', technical: 'responsable technique', coach: 'coach', supporter: 'bénévole' } as const;
const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
const youth = (category: string | null) => /^U(1[0-8]|[0-9])$/i.test(category ?? '');

const ERRORS: Record<string, string> = {
  used: 'Ce lien a déjà servi.',
  expired: 'Ce lien a expiré.',
  account_in_use: 'Ce compte est déjà relié à une autre fiche du club. Utilise une autre adresse, ou préviens ton coach.',
  already_linked: 'Cette fiche est déjà reliée à un autre compte. Préviens le club.',
  has_code_account: 'Cette fiche se connecte avec un code perso : demande-le à ton coach.',
  code_account: 'Un compte « code perso » ne se relie pas par lien. Utilise une adresse e-mail.',
  too_many: 'Trop d’adresses essayées avec ce lien : demande un nouveau lien.',
  wrong_email: 'Ce lien est réservé à l’adresse de la demande d’inscription.',
  minor: 'Ce lien de bénévole est pour un adulte : tes parents peuvent donner un coup de main avec leur propre compte.',
  input: 'Adresse e-mail invalide.',
  network: 'Pas de connexion au serveur.',
};
const errorText = (e?: string) => ERRORS[e ?? ''] ?? 'Impossible pour le moment. Réessaie dans un instant.';

export async function renderInviteLanding(root: HTMLElement, token: string, appBase: string): Promise<void> {
  const info = await inviteLinkInfo(token).catch(() => null);
  if (info) applyBrand({ id: `link:${token.slice(0, 8)}`, name: info.club.name, short_name: info.club.short_name, logo_url: info.club.logo, primary_color: info.club.color }, false);
  const brand = info ? clubMark({ id: 'x', name: info.club.name, short_name: info.club.short_name, logo_url: info.club.logo, primary_color: info.club.color }, 'lg') : html`<div class="ball" aria-hidden="true"></div>`;
  // Mentions légales et données du club (D-103) : <lien>/donnees (le sous-domaine du club : /donnees).
  const legal = info ? dataHref({ via: `/i/${token}` }) : dataHref();
  const shell = (body: Html, foot: Html | string = '') => mount(root, html`
    <main class="login iv-landing">
      <header class="login-brand">${brand}${info ? html`<p class="eyebrow">${info.club.name}${info.team ? ` · ${info.team}` : info.person_team ? ` · ${info.person_team}` : ''}</p>` : ''}</header>
      <section class="login-card">${body}</section>
      ${foot ? html`<p class="login-foot">${foot}</p>` : ''}
      <p class="powered">propulsé par Arceau</p>
      <p class="login-foot">${legalFootLink(legal)}</p>
    </main>`);
  const toApp = () => location.replace(`${appBase.replace(/\/$/, '')}/#/`);

  if (!info) { shell(html`<h1 class="iv-title">Lien introuvable</h1><p class="muted">Vérifie le lien reçu, ou demande-en un nouveau à la personne qui te l’a envoyé.</p>`); return; }
  if (info.status !== 'open') {
    shell(html`
      <h1 class="iv-title">${info.status === 'used' ? 'Ce lien a déjà servi' : info.status === 'expired' ? 'Ce lien a expiré' : 'Ce lien n’est plus actif'}</h1>
      ${info.email_hint ? html`<p class="muted">L’espace du club existe : connectez-vous avec l’adresse de votre demande (${info.email_hint}).</p>`
        : html`<p class="muted">Demande un nouveau lien à ${info.by}. Tu as déjà ton compte ? Connecte-toi.</p>`}
      <a class="btn btn-primary" href="${appBase.replace(/\/$/, '')}/#/">Se connecter</a>`);
    return;
  }
  if (info.kind === 'team') return renderJoin(info);
  return renderClaim(info);

  // --- Se présenter (lien d'équipe) ------------------------------------------------------
  function renderJoin(i: LinkInfo): void {
    const kid = youth(i.category);
    const title = i.friend ? `${i.by} t’invite à jouer avec les ${i.team}` : kid ? `Rejoindre les ${i.team}` : `Rejoins les ${i.team}`;
    const year = new Date().getFullYear();
    shell(html`
      <h1 class="iv-title">${title}</h1>
      <p class="muted">${kid ? 'Présentez votre enfant : le coach vous répond et propose une séance d’essai.' : 'Présente-toi : le coach te répond et te propose une séance d’essai.'}</p>
      <form class="login-form" id="f-join" novalidate>
        <div class="row-2">
          <label class="field"><span>${kid ? 'Prénom de l’enfant' : 'Prénom'}</span><input name="first_name" maxlength="60" required autocomplete="${kid ? 'off' : 'given-name'}"></label>
          <label class="field"><span>Nom</span><input name="last_name" maxlength="60" autocomplete="${kid ? 'off' : 'family-name'}"></label>
        </div>
        <label class="field"><span>Année de naissance</span><input name="birth_year" inputmode="numeric" maxlength="4" placeholder="${kid ? String(year - 11) : '2001'}" required></label>
        ${kid ? html`<label class="field"><span>Votre nom (parent)</span><input name="contact_name" maxlength="80" autocomplete="name"></label>` : ''}
        <label class="field"><span>${kid ? 'Votre e-mail' : 'Ton e-mail'}</span><input name="email" type="email" inputmode="email" autocomplete="email"></label>
        <label class="field"><span>${kid ? 'Votre téléphone' : 'Ton téléphone'}</span><input name="phone" type="tel" inputmode="tel" autocomplete="tel"></label>
        <label class="field"><span>Un mot pour le coach</span><textarea name="message" rows="2" maxlength="500" placeholder="${kid ? 'ex. elle a déjà joué un an à…' : 'ex. j’ai joué 3 ans à…'}"></textarea></label>
        <label class="check"><input type="checkbox" name="consent"><span>J’accepte que le club me recontacte au sujet de cette demande. Ces coordonnées ne servent qu’à ça.</span></label>
        ${consentNote(legal, !kid)}
        <p class="form-msg" role="alert"></p>
        <button class="btn btn-primary" type="submit">Envoyer ${kid ? 'la demande' : 'ma demande'}</button>
      </form>`, 'Coordonnées utilisées seulement pour répondre à cette demande.');
    const f = $<HTMLFormElement>(root, '#f-join')!;
    const msg = $(f, '.form-msg')!;
    f.addEventListener('submit', async e => {
      e.preventDefault();
      const d = new FormData(f);
      const s = (k: string) => String(d.get(k) ?? '').trim() || null;
      const by = Number(s('birth_year'));
      if (!s('first_name')) { msg.textContent = 'Le prénom, s’il te plaît.'; return; }
      if (!(by >= year - 90 && by <= year - 3)) { msg.textContent = 'Année de naissance : 4 chiffres, ex. 2001.'; return; }
      if (!s('email') && !s('phone')) { msg.textContent = 'Un e-mail ou un téléphone, pour que le coach réponde.'; return; }
      if (!d.get('consent')) { msg.textContent = 'Coche la case pour que le club puisse répondre.'; return; }
      const btn = $<HTMLButtonElement>(f, 'button[type=submit]')!;
      btn.disabled = true;
      try {
        await requestJoin(token, { first_name: s('first_name')!, last_name: s('last_name'), birth_year: by, contact_name: s('contact_name'),
          email: s('email'), phone: s('phone'), message: s('message'), consent: true });
        shell(html`<h1 class="iv-title">C’est envoyé ✓</h1>
          <p>${kid ? 'Le coach a reçu la demande et vous répond vite.' : 'Le coach a reçu ta demande et te répond vite.'}</p>
          ${i.friend ? html`<p class="muted">On prévient ${i.by} quand c’est validé.</p>` : ''}`);
      } catch (err) {
        btn.disabled = false;
        msg.textContent = String((err as Error).message).includes('trop') ? 'Beaucoup de demandes en peu de temps : réessaie plus tard.' : 'Envoi impossible pour le moment.';
      }
    });
  }

  // --- Relier son compte (lien personnel ou de rôle) ------------------------------------
  async function renderClaim(i: LinkInfo): Promise<void> {
    // Club créé en libre-service (D-088) : le lien est réservé à l'adresse de la demande. L'équipe gratuite (D-166) : le
    // coach qui vient de créer son équipe (lien de rôle réservé à son adresse), au tutoiement comme « Créer mon équipe ».
    const reserved = !!i.email_hint;
    const coachSelf = reserved && i.role === 'coach';
    const vous = !coachSelf && (i.relation === 'parent' || (i.kind === 'role' && i.role !== 'supporter'));
    const t = (tu: string, v: string) => (vous ? v : tu);
    // Un parent invité dans un club en mode gratuit (D-166, maquette g3) : ce qu'il lit avant d'accepter.
    const note: FreeLinkNote | null = i.kind === 'person' && i.relation === 'parent' ? await freeLinkNote(token).catch(() => null) : null;
    const her = note?.gender === 'F' ? 'elle' : 'lui';
    const head = coachSelf
      ? html`<h1 class="iv-title">Ton équipe est prête</h1>
          <p>Dernière étape : ton compte, à l’adresse de ta demande (<strong>${i.email_hint}</strong>). Tu entres comme coach des ${i.team ?? 'ton équipe'}.</p>`
      : reserved
      ? html`<h1 class="iv-title">L’espace ${deClub(i.club.name)} est prêt</h1>
          <p>Dernière étape : votre compte, à l’adresse de votre demande (<strong>${i.email_hint}</strong>). Vous entrez avec les droits du bureau.</p>`
      : note
      ? html`<h1 class="iv-title">Suivez ${i.person} dans son équipe</h1>
          <p>${note.coach ?? i.by}, coach des ${note.team ?? i.person_team ?? 'l’équipe'}, vous invite sur l’app de l’équipe : planning, convocations, absences, covoiturage.</p>
          <div class="eg-before" data-guide="eg-before"><p class="eyebrow">Avant d’accepter</p>
            <ul class="eg-have">
              <li><span class="ti eg-i-shield" aria-hidden="true"></span><span><b>Le club est responsable des données de ${i.person}</b><small>${cap(leClub(i.club.name))} ; Arceau les héberge. L’équipe est inscrite par son coach, et le club en est informé.</small></span></li>
              <li><span class="ti ti-eye" aria-hidden="true"></span><span><b>Ce que voit l’équipe</b><small>Son prénom, l’initiale de son nom, son numéro, ses présences. Vos coordonnées : le coach seulement.</small></span></li>
              ${note.under15 ? html`<li><span class="ti eg-i-lock" aria-hidden="true"></span><span><b>${i.person} a moins de 15 ans : vous acceptez pour ${her}</b><small>${cap(her)} n’aura son propre accès, par un code perso, que si vous le voulez.</small></span></li>` : ''}
              ${note.minor ? html`<li><span class="ti eg-i-star" aria-hidden="true"></span><span><b>Jamais de publicité</b><small>Ni offre de partenaire, ni bannière pour un mineur.</small></span></li>` : ''}
            </ul></div>`
      : i.kind === 'person'
      ? (i.relation === 'parent'
        ? html`<h1 class="iv-title">Suivez ${i.person} au club</h1><p>${i.by} vous invite sur l’app du club : planning, convocations, covoiturage, et vos réponses pour ${i.person}.</p>`
        : html`<h1 class="iv-title">${i.person}, ton équipe t’attend</h1><p>${i.by} t’invite sur l’app du club : entraînements, convocations, plays, prépa.</p>`)
      : i.role === 'supporter'
        ? html`<h1 class="iv-title">${i.name ? `${i.name}, merci` : 'Merci'} de donner un coup de main</h1>
            <p>${i.by} t’invite parmi les bénévoles du club${i.group ? html` : dans le groupe <strong>${i.group}</strong>` : ''}. Matchs, buvette, tournois : tu choisis tes créneaux.</p>`
        : html`<h1 class="iv-title">${i.name ? `${i.name}, ` : ''}bienvenue ${i.role === 'coach' ? `chez les ${i.team}` : 'au club'}</h1>
            <p>${i.by} vous invite comme ${i.role === 'coach' ? `coach des ${i.team}` : ROLE_WORD[i.role!]} sur l’app du club.</p>`;
    const foot = i.kind === 'person' && i.relation === 'self'
      ? `Ce lien est personnel, il ne sert qu’une fois. Tu n’es pas ${i.person} ? Préviens le club.`
      : 'Ce lien ne sert qu’une fois.';

    const { data } = supabase ? await supabase.auth.getSession() : { data: { session: null } };
    const session = data.session;
    if (session && reserved) {
      // Le compte de la demande, déjà ouvert ici : relié tout de suite. Un autre compte : on en sort.
      if (await link()) return;
      await supabase?.auth.signOut();
    } else if (session) {
      shell(html`${head}
        <p class="muted">Compte ouvert sur ce téléphone : <strong>${session.user.email ?? ''}</strong></p>
        <p class="form-msg" role="alert" id="iv-err"></p>
        <button class="btn btn-primary" type="button" id="iv-link">Relier à ce compte</button>
        <button class="btn btn-link" type="button" id="iv-other">Utiliser une autre adresse</button>`, foot);
      $(root, '#iv-link')!.addEventListener('click', () => void link());
      $(root, '#iv-other')!.addEventListener('click', async () => { await supabase?.auth.signOut(); void renderClaim(i); });
      return;
    }
    if (reserved && i.direct) {
      // Juste après la création : le compte s'ouvre sans code (l'e-mail de la demande a prouvé
      // l'adresse). Un compte qui existait déjà passe par le code, plus bas.
      shell(html`${head}<p class="muted">Ouverture de votre espace…</p><p class="form-msg" role="alert" id="iv-err"></p>`, foot);
      if ((await claimDirect(token)).ok) { await link(); return; }
    }
    askEmail();

    function askEmail(prefill = ''): void {
      shell(html`${head}
        <form class="login-form" id="f-mail" novalidate>
          <label class="field"><span>${t('Ton adresse e-mail', 'Votre adresse e-mail')}</span>
            <input name="email" type="email" autocomplete="email" inputmode="email" placeholder="prenom@exemple.fr" value="${prefill}" required></label>
          ${note ? html`<label class="check"><input type="checkbox" name="accept"><span>Je suis le parent de ${i.person} et j’accepte pour ${her}. J’ai lu les <a href="${legal}">mentions légales et données</a>.</span></label>` : ''}
          <p class="form-msg" role="alert"></p>
          <button class="btn btn-primary" type="submit">Recevoir mon code</button>
          <p class="hint">Pas de mot de passe : un code à usage unique arrive par e-mail. ${i.kind === 'person' && i.relation === 'self' ? 'Ton compte est relié à ta fiche de l’équipe.' : note ? 'Vous pouvez quitter l’équipe à tout moment.' : ''}</p>
        </form>`, foot);
      const f = $<HTMLFormElement>(root, '#f-mail')!;
      $<HTMLInputElement>(f, 'input')!.focus();
      f.addEventListener('submit', async e => {
        e.preventDefault();
        const email = String(new FormData(f).get('email') ?? '').trim().toLowerCase();
        const msg = $(f, '.form-msg')!, btn = $<HTMLButtonElement>(f, 'button[type=submit]')!;
        if (note && !new FormData(f).get('accept')) { msg.textContent = `Cochez la case : vous acceptez pour ${i.person}.`; return; }
        btn.disabled = true; msg.textContent = '';
        const r = await claimLink(token, email);
        if (r.error) { btn.disabled = false; msg.textContent = errorText(r.error); return; }
        const sent = await sendEmailCode(email);
        if (!sent.ok) { btn.disabled = false; msg.textContent = sent.message; return; }
        askCode(email);
      });
    }

    function askCode(email: string): void {
      shell(html`${head}
        <form class="login-form" id="f-otp" novalidate>
          <p class="lead">Un code à 6 chiffres vient de partir à <strong>${email}</strong>.</p>
          <label class="field"><span>Code reçu par e-mail</span>
            <input name="token" inputmode="numeric" pattern="[0-9]*" maxlength="6" autocomplete="one-time-code" placeholder="••••••" class="otp" required></label>
          <p class="form-msg" role="alert"></p>
          <button class="btn btn-primary" type="submit">Entrer dans l’app</button>
          <button class="btn btn-link" type="button" id="b-change">Changer d’adresse</button>
        </form>`, foot);
      const f = $<HTMLFormElement>(root, '#f-otp')!;
      $<HTMLInputElement>(f, 'input')!.focus();
      $(root, '#b-change')!.addEventListener('click', () => askEmail(email));
      f.addEventListener('submit', async e => {
        e.preventDefault();
        const msg = $(f, '.form-msg')!, btn = $<HTMLButtonElement>(f, 'button[type=submit]')!;
        btn.disabled = true; msg.textContent = '';
        const v = await verifyEmailCode(email, String(new FormData(f).get('token') ?? ''));
        if (!v.ok) { btn.disabled = false; msg.textContent = v.message; return; }
        await link(msg, btn);
      });
    }

    async function link(msgEl?: HTMLElement, btn?: HTMLButtonElement): Promise<boolean> {
      const msg = msgEl ?? $(root, '#iv-err');
      if (btn) btn.disabled = true;
      const r = await claimLink(token);
      if (r.error) { if (btn) btn.disabled = false; if (msg) msg.textContent = errorText(r.error); return false; }
      shell(html`<h1 class="iv-title">C’est fait ✓</h1><p>${t('Ton compte est prêt. On t’ouvre l’app…', 'Votre compte est prêt. Ouverture de l’app…')}</p>`);
      setTimeout(toApp, 900);
      return true;
    }
  }
}
